/**
 * FullStory utility functions for debugging and verification
 */

declare global {
  interface Window {
    FS?: {
      event: (eventName: string, properties?: Record<string, unknown>) => void;
      identify: (uid: string, userVars?: Record<string, unknown>) => void;
      setUserVars: (userVars: Record<string, unknown>) => void;
      getCurrentSessionURL: (now?: boolean) => string | null;
      // Add other FullStory methods as needed
    };
  }
}

/**
 * Check if FullStory is properly initialized and available
 * @returns true if FullStory is available, false otherwise
 */
export function isFullStoryAvailable(): boolean {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    
    // Check if the FullStory object is available on window
    // Note: typeof null === 'object', so we need to explicitly check for null
    const hasFS = 'FS' in window && typeof window.FS === 'object' && window.FS !== null;
    
    return hasFS;
  } catch (error) {
    console.error('[FullStory] Error checking availability:', error);
    return false;
  }
}

/**
 * Get the current FullStory session URL for debugging
 * @returns The session URL or null if not available
 */
export function getFullStorySessionUrl(): string | null {
  try {
    if (!isFullStoryAvailable() || !window.FS?.getCurrentSessionURL) {
      return null;
    }
    return window.FS.getCurrentSessionURL(true);
  } catch (error) {
    console.error('[FullStory] Error getting session URL:', error);
    return null;
  }
}

/**
 * Verify FullStory is working by sending a test event
 * @param testEventName - Optional custom test event name
 * @returns true if test event was sent successfully
 */
export function testFullStoryEvent(testEventName: string = 'FullStoryTest'): boolean {
  try {
    if (!isFullStoryAvailable() || !window.FS?.event) {
      console.warn('[FullStory] Cannot send test event - FullStory not available');
      return false;
    }
    
    window.FS.event(testEventName, {
      timestamp: new Date().toISOString(),
      test: true,
    });
    
    console.log(`[FullStory] Test event "${testEventName}" sent successfully`);
    return true;
  } catch (error) {
    console.error('[FullStory] Error sending test event:', error);
    return false;
  }
}
